// Escritura de pedidos de venta contra Dataverse (entidades virtuales de F&O),
// que es síncrona: lo creado o editado se ve al instante.
//
// Alimenta el formulario de alta/edición que usa la página de Pedidos de venta.
// El listado no vive aquí: lo sirve usePedidosVentas (Fabric + overlay).
// Artículos y dimensiones vienen de Fabric; clientes y direcciones de Dataverse.
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ordenesVentaApi } from '@/services/api'
import type { DireccionNuevaForm, PedidoVentaCabeceraForm, PedidoVentaLineaForm } from '@/types'

export function useOrdenesServicio() {
  const qc = useQueryClient()

  /** El listado vive en usePedidosVentas (Fabric + overlay). Tras escribir hay
   *  que refrescar las dos fuentes: el overlay verá el cambio enseguida, Fabric
   *  dentro de ~15 min. */
  const invalidar = (numero?: string) => {
    qc.invalidateQueries({ queryKey: ['pedidos-ventas'] })
    qc.invalidateQueries({ queryKey: ['pedidos-ventas-recientes'] })
    if (numero) {
      qc.invalidateQueries({ queryKey: ['ordenes-venta-detalle', numero] })
      qc.invalidateQueries({ queryKey: ['venta-lineas-recientes', numero] })
    }
  }

  const crear = useMutation({
    mutationFn: ({ cab, lineas }: { cab: PedidoVentaCabeceraForm; lineas: PedidoVentaLineaForm[] }) =>
      ordenesVentaApi.crear(cab, lineas),
    onSuccess: (res) => invalidar(res.pedido.numero),
  })

  const actualizar = useMutation({
    mutationFn: ({ numero, cab, lineas }: { numero: string; cab: PedidoVentaCabeceraForm; lineas: PedidoVentaLineaForm[] }) =>
      ordenesVentaApi.actualizar(numero, cab, lineas),
    onSuccess: (res) => invalidar(res.pedido.numero),
  })

  const solicitarCancelacion = useMutation({
    mutationFn: ({ numero, empresa, motivo }: { numero: string; empresa?: string; motivo?: string }) =>
      ordenesVentaApi.solicitarCancelacion(numero, empresa, motivo),
  })

  return { crear, actualizar, solicitarCancelacion }
}

/** Detalle de un pedido (líneas + GUIDs de edición). Se carga al expandir la
 *  fila o al abrir el formulario de edición. */
export function usePedidoDetalle(numero: string, empresa?: string, enabled = true) {
  return useQuery({
    queryKey: ['ordenes-venta-detalle', numero, empresa ?? ''],
    queryFn: () => ordenesVentaApi.getDetalle(numero, empresa),
    enabled: enabled && !!numero,
    staleTime: 30 * 1000,
  })
}

/** Búsqueda de artículos en Fabric (real). `enabled` la controla el picker (abre al enfocar).
 *  Con q vacío el backend devuelve el top de artículos para mostrar opciones iniciales. */
export function useArticulosBusqueda(q: string, enabled = true) {
  return useQuery({
    queryKey: ['ov-articulos', q],
    queryFn: () => ordenesVentaApi.buscarArticulos(q),
    enabled,
    staleTime: 60 * 1000,
  })
}

/** Búsqueda de clientes en Dataverse. `enabled` la controla el picker (abre al enfocar). */
export function useClientesBusqueda(q: string, enabled = true) {
  return useQuery({
    queryKey: ['ov-clientes', q],
    queryFn: () => ordenesVentaApi.buscarClientes(q),
    enabled,
    staleTime: 60 * 1000,
  })
}

/** Cliente al que amarra el rol (cliente externo), o null si elige libremente.
 *  Provisional, hasta que haya un sistema de roles propio. */
export function useClienteFijo() {
  return useQuery({
    queryKey: ['ov-cliente-fijo'],
    queryFn: ordenesVentaApi.clienteFijo,
    staleTime: 60 * 60 * 1000,
  })
}

/** Alta de dirección de envío sobre un cliente existente. Invalida la lista de
 *  direcciones de ese cliente. */
export function useCrearDireccion(cuenta: string, empresa?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (form: DireccionNuevaForm) => ordenesVentaApi.crearDireccion(cuenta, form),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['ov-direcciones', cuenta, empresa ?? ''] }),
  })
}

/** Combinaciones color/estilo de un artículo (Fabric). Lazy por línea. */
export function useDimensionesArticulo(itemid: string, empresa?: string, enabled = true) {
  return useQuery({
    queryKey: ['ov-dim', empresa ?? '', itemid],
    queryFn: () => ordenesVentaApi.dimensiones(itemid, empresa),
    enabled: enabled && !!itemid,
    staleTime: 5 * 60 * 1000,
  })
}

/** Direcciones de envío reales del cliente (Dataverse). */
export function useDirecciones(cuenta: string, empresa?: string) {
  return useQuery({
    queryKey: ['ov-direcciones', cuenta, empresa ?? ''],
    queryFn: () => ordenesVentaApi.direcciones(cuenta, empresa),
    enabled: !!cuenta,
    staleTime: 60 * 1000,
  })
}
